import type { CharacterItem, DramaArticle, NewsItem, UnifiedContent } from "@/types/content";
import { dramas } from "@/mocks/dramas";
import { characters } from "@/mocks/characters";
import { news } from "@/mocks/news";

export const allDramas = dramas;
export const allCharacters = characters;
export const allNews = news;

export function getAllUnified(): UnifiedContent[] {
  const dramaItems: UnifiedContent[] = dramas.map((item) => ({
    id: item.id,
    category: "drama",
    title: item.title,
    summary: item.summary,
    cover: item.cover,
    date: item.date,
    tags: item.tags,
  }));

  const characterItems: UnifiedContent[] = characters.map((item) => ({
    id: item.id,
    category: "characters",
    title: item.name,
    summary: item.summary,
    cover: item.avatar,
    date: "",
    tags: [item.role, item.actor],
  }));

  const newsItems: UnifiedContent[] = news.map((item) => ({
    id: item.id,
    category: "news",
    title: item.title,
    summary: item.summary,
    cover: item.cover,
    date: item.date,
    tags: item.tags,
  }));

  return [...dramaItems, ...characterItems, ...newsItems];
}

export function searchContent(keyword: string): UnifiedContent[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];

  const dramaText = new Map(dramas.map((item) => [item.id, item.content.join(" ")]));
  const newsText = new Map(news.map((item) => [item.id, item.content.join(" ")]));
  const characterText = new Map(characters.map((item) => [item.id, item.background.join(" ")]));

  return getAllUnified().filter((item) => {
    const body =
      item.category === "drama"
        ? dramaText.get(item.id) || ""
        : item.category === "news"
          ? newsText.get(item.id) || ""
          : characterText.get(item.id) || "";

    const haystack = `${item.title} ${item.summary} ${item.tags.join(" ")} ${body}`.toLowerCase();
    return haystack.includes(query);
  });
}

export function getRelated(category: string, id: string, limit = 5): UnifiedContent[] {
  const pool = getAllUnified().filter((item) => item.category === category && item.id !== id);
  const others = getAllUnified().filter((item) => item.category !== category);
  return [...pool, ...others].slice(0, limit);
}

export function findDrama(id: string): DramaArticle | undefined {
  return dramas.find((item) => item.id === id);
}

export function findCharacter(id: string): CharacterItem | undefined {
  return characters.find((item) => item.id === id);
}

export function findNews(id: string): NewsItem | undefined {
  return news.find((item) => item.id === id);
}

export function formatDate(date: string): string {
  if (!date) return "";
  const [y, m, d] = date.split("-");
  if (!y || !m || !d) return date;
  return `${y} 年 ${Number(m)} 月 ${Number(d)} 日`;
}